import React, { useCallback, useEffect, useRef, useState } from 'react'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import { notify, prefix } from '../../../utils/index_tagging_tool'
import { api } from '../../../utils/index'
import DimensionDetailForm from './DimensionDetailForm'
import DimensionSave from './DimensionSave'
import _ from 'lodash'
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap'
import Header from '../../../components/Headers/Header'
import { Tab, Tabs } from 'react-bootstrap'
import DimensionRuleList from './DimensionRuleList'
import { getCustomDimensionTags, getCustomRules, getSavedDimensionTags } from '../../api_methods'
import LanguageSelector from '../../components/LanguageSelector'

// Helper function per costruire la query string
const buildQueryString = (params) => {
  let querySearch = '';
  if (params?.query) {
    querySearch += '&query=' + params.query;
  }
  if (params?.queryContiene) {
    querySearch += '&querycontiene=' + params.queryContiene;
  }
  if (params?.queryNonContiene) {
    querySearch += '&querynoncontiene=' + params.queryNonContiene;
  }
  if (params?.lang) {
    querySearch += '&lang=' + params.lang;
  }
  return querySearch;
};

function DimensionDetail({ match, location }) {
  const componentId = match.params.id;
  const isSavedPath = location.pathname.includes('saved');
  const pathDetailType = location.pathname.split('/')[2];
  const pathContainerType = pathDetailType === 'custom-dimensions' ? 'reports' : 'dimension-groups'

  // Stati Essenziali
  const [id, setId] = useState(null);
  const [name, setName] = useState('');
  const [, setDisplayName] = useState('');
  const [reportName, setReportName] = useState('');
  const [language, setLanguage] = useState('it');
  const [sourceId, setSourceId] = useState(null);
  const [linked, setLinked] = useState(null);
  const [dependencyDimensions, setDependencyDimensions] = useState([]);
  const [dependencyDimensionsTags, setDependencyDimensionsTags] = useState({});
  const [saved] = useState(isSavedPath);
  const [detailType] = useState(pathDetailType);

  // Stati Regole e UI
  const [otherDimensions, setOtherDimensions] = useState([]);
  const [rules, setRules] = useState([]);
  const [rulePages, setRulePages] = useState(1);

  // Stati di Caricamento
  const [loading, setLoading] = useState(true); // Loading generale iniziale
  const [loader, setLoader] = useState(false); // Loader specifico regole/form

  const listRef = useRef(null);

  // --- Funzioni Gestione Regole (useCallback per memoizzazione) ---
  const updateDependenciesDimensions = useCallback((dimension, isChecked) => {
    	const name = dimension.display_name;

    	// Using a functional update ensures we have the latest state.
    	setDependencyDimensions(currentFilters => {
    	  if (isChecked) {
    	    // Add the dimension name to the array if it's not already there
    	    return [...currentFilters, name];
    	  } else {
    	    // Remove the dimension name from the array
    	    return currentFilters.filter(item => item !== name);
    	  }
    	});
      // download tags only if needed
      if(!dependencyDimensionsTags.hasOwnProperty(name)) {
        otherDimensions.filter(dim => dim.display_name === name).map(
          (dim) => getCustomDimensionTags(dim.id).then(
            (data) => setDependencyDimensionsTags({...dependencyDimensionsTags, [dim.display_name]: data})
          )
        )
      }

  }, [otherDimensions,dependencyDimensionsTags]);

  const createNewRule = useCallback((index, type = 'custom') => ({
    id: -1,
    report_id: type === 'custom' ? 'fakeid' : null,
    index: index,
    priority: index,
    tag_it: '',
    pos_regex: '',
    dependencies: [],
  }), []); // Manteniamo operators qui perché la regola dipende da esso

  const handleFirstRule = useCallback(() => {
    // Aggiunge una regola vuota SOLO SE non ci sono già regole
    setRules(prevRules => {
        if (prevRules.length === 0) { // Controllo esplicito su operators
            return [createNewRule(0)];
        }
        return prevRules; // Non fare nulla altrimenti
    });
  }, [createNewRule]); // Dipende da operators

  const createRule = useCallback((e, currentRules, type = 'custom') => {
    const newRule = createNewRule(currentRules.length, type);
    setRules(prevRules => {
       const updatedRules = [...prevRules, newRule];
       setTimeout(() => {
            if (listRef.current) {
                 listRef.current.scrollToItem(updatedRules.length -1);
            }
        }, 0);
       return updatedRules;
    });
  }, [createNewRule, listRef]); // Rimosso operators se non vogliamo bloccare l'aggiunta

  const removeRule = (idToRemove) => {
    if (rules.length <= 1) {
      notify('Deve esistere almeno una regola', 'warning');
      return;
    }
    const updatedRules = rules.filter((rule) => rule.id !== idToRemove);
    setRules(updatedRules);
  };

  const reorderRules = useCallback((order) => {
    setRules(prevRules => {
      const sortedRules = _.orderBy(prevRules, [order.field], [order.order]);
      return sortedRules.map((rule, i) => ({ ...rule, index: i }));
    });
  }, []);


  // --- Funzioni Fetch Dati ---

  const fetchCurrentDimensionRules = useCallback(async (page, query = '', queryParams = null) => {
    if (!id) return;
    // Non mettiamo setLoader qui, sarà gestito da chi chiama questa funzione
    try {
      const res = await getCustomRules(id, saved, page, query);
      const data = res.data;
      const fetchedRules = data.results.map((x, i) => ({ ...x, index: i }));

      setRules(fetchedRules);
      setRulePages(Math.ceil(data.count / 500));

      if (fetchedRules.length === 0 && !queryParams) {
          // Prova ad aggiungere la prima regola (handleFirstRule controllerà gli operatori)
          handleFirstRule();
      } else if (fetchedRules.length === 0 && queryParams) {
          notify("Non vi sono regole che corrispondono ai filtri", "warning");
      }
    } catch (error) {
      notify("Errore nel caricamento delle regole", "error");
      setRules([]);
      handleFirstRule(); // Prova ad aggiungere la regola vuota
    }
    // Non mettiamo setLoader(false) qui
  }, [id, saved, handleFirstRule]); // handleFirstRule dipende da operators, quindi indirettamente questo dipende da operators


  const fetchRules = useCallback(async (page, queryParams = null) => {
    if (!id) return;

    setLoader(true); // Mostra il loader specifico del form
    // Per le saved-dimensions manteniamo sempre la lingua corrente anche
    // nelle ricerche/paginazione, altrimenti il backend torna implicitamente a "it".
    const effectiveParams = {
      ...(queryParams || {}),
      ...(saved && language ? { lang: queryParams?.lang || language } : {}),
    };
    const querySearch = buildQueryString(effectiveParams);

    try {
      await fetchCurrentDimensionRules(page, querySearch, queryParams);
    } catch (error) {
        console.error("Errore durante fetchRules:", error);
        // L'errore è già notificato da fetchCurrentDimensionRules
    } finally {
      setLoader(false); // Nascondi il loader specifico alla fine
    }
  }, [id, fetchCurrentDimensionRules, saved, language]);

  const updateLanguage = useCallback(async (lang) => {
    setLanguage(lang)
    fetchRules(1, {lang:lang})
  }, [fetchRules]);


  // --- Effetto Mount (SOLO DATI ESSENZIALI) ---
  useEffect(() => {
    document.title = 'Tagging tool - Caricamento...';
    setLoading(true);

    const fetchEssentialData = async () => {
      try {
        const res1 = await api.get(`${prefix}${pathDetailType}/${componentId}/`);
        const detailData = res1.data;

        // Imposta stati essenziali (operators può essere [])
        setName(detailData.name);
        setDisplayName(detailData.display_name);
        setId(detailData.id);
        setReportName(detailData.report);
        setSourceId(detailData.source_id);
        setLinked(detailData.linked);
        document.title = detailData.name || 'Tagging tool';

        // get report or dimensions-group data
        const res2 = await api.get(`${prefix}${pathContainerType}/${detailData.source_id}/`);
        const containerData = res2.data;
        let containerDimensions = containerData.dimensions;
        setOtherDimensions(containerDimensions.filter(dimension => dimension.id !== detailData.id));
        setDependencyDimensions(containerDimensions.filter(
          dimension => detailData.dependencies.includes(dimension.id) || detailData.dependencies.includes(dimension.linked)
        ).map((d) => d.display_name));

        // get list of tags for dependency dimensions
        if (detailData.dependencies.length > 0) {
          const dependencyDimensionsToFetch = containerData.dimensions.filter(
              dimension => detailData.dependencies.includes(dimension.id) || detailData.dependencies.includes(dimension.linked)
          );

          // Fire off all API calls concurrently
          const promises = dependencyDimensionsToFetch.map(dimension => {
              const apiCall = saved ? getSavedDimensionTags : getCustomDimensionTags;
              return apiCall(dimension.id).then(tags => ({
                  // Return an object with the dimension name and its tags
                  displayName: dimension.display_name,
                  tags: tags
              }));
          });

          // Wait for all promises to resolve
          const results = await Promise.all(promises);

          // Now, update the state in a single, clean operation
          const newTags = {};
          results.forEach(result => {
              newTags[result.displayName] = result.tags;
          });

          // Using functional update is still best practice, though not strictly
          // necessary here since we're setting the whole object at once.
          setDependencyDimensionsTags(prevState => ({
              ...prevState, // In case there was some other state we wanted to preserve
              ...newTags
          }));
        }

      } catch (error) {
          console.error("Errore nel caricamento dati essenziali:", error);
          notify("Errore nel caricamento dei dati iniziali", "error");
          document.title = "Tagging tool - Errore";
      } finally {
          setLoading(false); // Disattiva loading generale SEMPRE
      }
    };
    fetchEssentialData();
  }, [componentId, pathDetailType]);


  // --- Effetto per fetchare REGOLE INIZIALI e ALTRE DIMENSIONI ---
  useEffect(() => {
    // Esegui solo se l'ID è disponibile e il loading iniziale è terminato
    if (!loading) {
        setLoader(true); // Attiva loader specifico

        const fetchInitialRulesAndDims = async () => {
            try {
                // Fetch regole iniziali (pagina 1, no filtri)
                // fetchCurrentDimensionRules proverà a chiamare handleFirstRule,
                // che a sua volta controllerà se ci sono operatori prima di aggiungere la regola vuota.
                await fetchCurrentDimensionRules(1, '', null);

            } catch (error) {
                console.error("Errore nel caricamento regole/dimensioni iniziali:", error);
                // Non mostrare notifiche duplicate se fetchCurrentDimensionRules fallisce
                if (!error.message?.includes("Errore nel caricamento delle regole")) { // Evita doppia notifica
                     notify("Errore nel caricamento iniziale delle regole o altre dimensioni", "error");
                }
                setRules([]); // Resetta regole
                handleFirstRule(); // Prova ad aggiungere la prima regola (se ci sono operatori)
            } finally {
                setLoader(false); // Disattiva loader specifico SEMPRE
            }
        };
        fetchInitialRulesAndDims();
    }
  }, [id, loading, detailType, fetchCurrentDimensionRules, handleFirstRule]);


  // --- Funzione Link Altre Dimensioni ---
  const getOtherDimensionLink = (d) => {
    if (detailType === 'saved-dimensions')
      return `/reporting/saved-dimensions/${d.id}`;
    return `/reporting/custom-dimensions/${d.id}`;
  };

  const ruleStateManager = {
    rules: rules,
    create: createRule,
    fetch: fetchRules,
    remove: removeRule,
    pages: rulePages,
    reorder: reorderRules,
  }

  // --- Render ---
  if (loading) {
    return <Loading />;
  }

  // Mostra errore se ID non caricato (potrebbe succedere se il primo fetch fallisce gravemente)
  if (!id) {
      return (
           <div className="AppTaggingTool ml-3 mr-3">
               <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
               <Card className="shadow bg-secondary p-5 text-center">
                   <CardHeader className="bg-white border-0">
                       <h2 className="text-muted">Errore</h2>
                   </CardHeader>
                   <p className="mt-3">Impossibile caricare i dati della dimensione.</p>
               </Card>
           </div>
      );
  }
  // Render principale
  return (
    <>
      <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
      <div className={'AppTaggingTool'} style={{ marginBottom: '100px' }}>
        <Card className="shadow bg-secondary">
          <CardHeader className="bg-white justify-content-between">
            <Row>
            <Col>
            <PageTitle
              title={name}
              link={reportName ? `/reporting/reports/seo/${sourceId}` : `/reporting-dimension-groups`}
              linkText={reportName ? 'Torna al report' : 'Torna alla lista dei gruppi'}
            />
            </Col>
            <Col md={2} className="align-content-center">
              {!reportName && <LanguageSelector language={language} setLanguage={updateLanguage}/>}
            </Col>
            </Row>
          </CardHeader>
          <CardBody>
          <Tabs defaultActiveKey="rulesEditor">
            <Tab title={"✏️ Editor Regole"} eventKey="rulesEditor">
              <DimensionDetailForm
                ruleStateManager={ruleStateManager}
                loader={loader}
                detailId={id}
                detailLinkedId={linked ? linked.id : null}
                detailReport={reportName}
                detailType={detailType}
                otherDimensions={otherDimensions}
                dependencyDimensions={dependencyDimensions}
                dependencyDimensionsTags={dependencyDimensionsTags}
                updateDependenciesDimensions={updateDependenciesDimensions}
                listRef={listRef}
              />
            </Tab>
            <Tab title="🏷️ Elenco Tag" eventKey="tags">
              <DimensionRuleList values={ruleStateManager.rules}/>
            </Tab>
            <Tab title="🧩 Altre Dimensioni" eventKey="dimensions">
            {/* Sezione Altre Dimensioni */}
            {otherDimensions.length > 0 && (
              <>
                <Row className="ml-5 mt-3"><h4>Altre dimensioni</h4></Row>
                <Row className="ml-5 mr-5 mb-3">
                  {otherDimensions.map((d) => (
                    <Button tag="a" href={getOtherDimensionLink(d)} className="btn-white mr-2 mb-2" size="sm" key={d.id} id={d.name}>
                        {d.name}
                    </Button>
                  ))}
                </Row>
              </>
            )}
            </Tab>

            {/* Sezione Link / Salva */}
            {linked && <Tab title="🔗 Dimensione Collegata" eventKey="linkedDimension">
              <strong>
                <a href={`/reporting/saved-dimensions/${linked.id}`} title={'Dimensione salvata collegata'}>
                  🔗 {linked.display_name}
                </a>
              </strong>
            </Tab>}
            {detailType !== 'saved-dimensions' && id && <Tab title="💾 Salva Dimensione" eventKey="save">
              <DimensionSave origin={id} name={name} reportName={reportName}/>
            </Tab>}
          </Tabs>
            </CardBody>
        </Card>
      </div>
    </>
  );
}

export default DimensionDetail;
